import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MOCK_CATEGORY_TARGET_EQUIPMENT_OPTIONS } from "../../../mock-data/company-detail";
import KpiSlotModal, { type KpiSlotOption } from "./KpiSlotModal";
import { AdminKpiTrendSection } from "./AdminKpiTrendSection";
import { KpiReportSettingsSection } from "./KpiReportSettingsSection";
import { adminKpiApiService } from "../../../../infrastructure/api/admin/admin-kpi-api-service";
import { adminMachineUseCase } from "../../../../di/container";
import {
  AGGREGATION_INTERVAL_LABELS,
  AggregationInterval,
  FACTORY_LOG_USE_SE_LABELS,
  KPI_TYPE_LABELS,
  isKpi1Type,
  ManualReportTarget,
  PLUGIN_TYPE_LABELS,
  PluginType,
  SBIZ_TYPE_LABELS,
  DISCLOSURE_TEMPLATES,
} from "../../../../domain/types/kpi";
import type {
  KpiCalendarCell,
  KpiCalendarStatus,
  Plugin,
  KpiReportLog,
  KpiReportLogStatus,
  KpiReportSummary,
  KpiSlot,
  KpiType,
  FactoryLog,
  FactoryLogStatus,
  SbizType,
  UpdateKpiSlotRequest,
} from "../../../../domain/types/kpi";
import type { MachineDetailDto } from "../../../../domain/types/machine";
import {
  CATEGORY_PERIOD_DURATION_OPTIONS,
  type CategoryPeriodDuration,
  addYears,
  durationToYears,
  inferDurationFromDates,
} from "./category-period-utils";

interface CategoryContentProps {
  program: Plugin;
  companyId: number;
  onRemove: () => void;
  onProgramUpdated: () => void;
}

// ─── badge helpers ───────────────────────────────────────────────────────────
const BlueBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
    style={{ background: "#E8EFFF", color: "#4285F4" }}
  >
    {children}
  </span>
);

const SuccessBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
    style={{ background: "#E6F4EA", color: "#1F7A3F" }}
  >
    {children}
  </span>
);

const WarnBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
    style={{ background: "#FFF4E5", color: "#B26A00" }}
  >
    {children}
  </span>
);

const CALENDAR_BG: Record<KpiCalendarStatus, string> = {
  SUCCESS: "#27AE60",
  FAILURE: "#F24A4A",
  PENDING: "#E0E0E0",
  EMPTY: "transparent",
};

const ReportLogStatusBadge: React.FC<{ status: KpiReportLogStatus }> = ({
  status,
}) =>
  status === "SUCCESS" ? (
    <SuccessBadge>SUCCESS</SuccessBadge>
  ) : (
    <WarnBadge>CALCULATION_FAILED</WarnBadge>
  );

const FactoryLogStatusBadge: React.FC<{ status: FactoryLogStatus }> = ({
  status,
}) => {
  if (status === "SUCCESS") return <SuccessBadge>SUCCESS</SuccessBadge>;
  if (status === "SHADOW") return <BlueBadge>SHADOW</BlueBadge>;
  return <WarnBadge>TRANSMISSION_FAILED</WarnBadge>;
};

// ─── 수동 보고 타겟 select 옵션 (mockup optgroup 구조 그대로 7종) ───────────
const MANUAL_REPORT_TARGET_OPTIONS: Array<{
  group: string;
  options: Array<{ value: ManualReportTarget; label: string }>;
}> = [
  {
    group: "LEVEL 1 — 가동 Y/N",
    options: [{ value: ManualReportTarget.LEVEL1, label: "LEVEL 1 재송신" }],
  },
  {
    group: "KPI 1 (P 슬롯)",
    options: [
      { value: ManualReportTarget.KPI1_ALL, label: "전체 (LEVEL 2 + LEVEL 3)" },
      { value: ManualReportTarget.KPI1_LEVEL2, label: "LEVEL 2 만" },
      { value: ManualReportTarget.KPI1_LEVEL3, label: "LEVEL 3 만" },
    ],
  },
  {
    group: "KPI 2 (D·Q 슬롯)",
    options: [
      { value: ManualReportTarget.KPI2_ALL, label: "전체 (LEVEL 2 + LEVEL 3)" },
      { value: ManualReportTarget.KPI2_LEVEL2, label: "LEVEL 2 만" },
      { value: ManualReportTarget.KPI2_LEVEL3, label: "LEVEL 3 만" },
    ],
  },
];

// 최근 보고 로그 기본 페이지 사이즈
const REPORT_LOGS_PAGE_SIZE = 10;

// 한국 시각 기준 yyyy-MM 표기 ("2026-06" 등)
const formatYearMonthLabel = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, "0")}`;

// 한국 시각 기준 yyyy-MM-dd 기본 일자 (수동 보고 대상 일자 기본값 = 어제)
const getDefaultManualReportDate = (): string => {
  const now = new Date();
  now.setDate(now.getDate() - 1);
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// KPI 그룹 분류 — WORKSHOP 은 kpiFldCd(P → KPI1), FACTORY 는 kpiFldCd 가 없어(null) kpiType 그룹으로 판정.
const isKpi1Slot = (slot: KpiSlot): boolean =>
  slot.kpiFldCd != null ? slot.kpiFldCd === "P" : isKpi1Type(slot.kpiType);

// 슬롯 추가시 default 값 (백엔드 sbizType 별 기본 단위/주기, SMART_WORKSHOP 전용)
const DEFAULT_SLOT_DEFAULTS: Record<
  string,
  { unit: string; aggregationInterval: AggregationInterval }
> = {
  OPERATION_COUNT: { unit: "건", aggregationInterval: AggregationInterval.HOURLY },
  OUTPUT_QUANTITY: { unit: "장/시간", aggregationInterval: AggregationInterval.HOURLY },
  SHEET_PRODUCTION: { unit: "장/시간", aggregationInterval: AggregationInterval.HOURLY },
  BOOK_PRODUCTION: { unit: "권/시간", aggregationInterval: AggregationInterval.HOURLY },
  OPERATION_RATE: { unit: "%", aggregationInterval: AggregationInterval.DAILY },
  ORDER_SHIPMENT_LEAD_TIME: { unit: "시간", aggregationInterval: AggregationInterval.DAILY },
  DELIVERY_COMPLIANCE_RATE: { unit: "%", aggregationInterval: AggregationInterval.DAILY },
  REVISION_REDUCTION: { unit: "건", aggregationInterval: AggregationInterval.DAILY },
};

// 슬롯 추가시 default 값 (백엔드 kpiType 별 기본 단위/주기, SMART_FACTORY 전용 — 정부 보고 없이 MES 통계만)
const DEFAULT_FACTORY_SLOT_DEFAULTS: Record<
  string,
  { unit: string; aggregationInterval: AggregationInterval }
> = {
  SHEET_PRODUCTION: { unit: "장/시간", aggregationInterval: AggregationInterval.HOURLY },
  BOOK_PRODUCTION: { unit: "권/시간", aggregationInterval: AggregationInterval.HOURLY },
  OPERATING_TIME: { unit: "시간", aggregationInterval: AggregationInterval.DAILY },
  ORDER_SHIPMENT_LEAD_TIME: { unit: "시간", aggregationInterval: AggregationInterval.DAILY },
  DELIVERY_COMPLIANCE_TIME: { unit: "일", aggregationInterval: AggregationInterval.DAILY },
  REVISION_REDUCTION: { unit: "건", aggregationInterval: AggregationInterval.DAILY },
};

// 슬롯 추가 모달 지표 옵션 — SMART_WORKSHOP(sbizType, 정부 코드 있음)
const WORKSHOP_P_OPTIONS: KpiSlotOption[] = [
  { value: "SHEET_PRODUCTION", group: "P", code: "B", label: SBIZ_TYPE_LABELS.SHEET_PRODUCTION, desc: "매엽지 생산량 (포스터/명함/전단지/스티커/걸이배너/스탠드배너)", unit: "장", direction: "INCREASE", needsMachine: true },
  { value: "BOOK_PRODUCTION", group: "P", code: "B", label: SBIZ_TYPE_LABELS.BOOK_PRODUCTION, desc: "책자 생산량 (소프트커버/하드커버)", unit: "권", direction: "INCREASE", needsMachine: true },
  { value: "OPERATION_RATE", group: "P", code: "A", label: SBIZ_TYPE_LABELS.OPERATION_RATE, desc: "실가동시간 / 부하시간 × 100", unit: "%", direction: "INCREASE", needsMachine: true, needsDailyTarget: true },
];
const WORKSHOP_DQ_OPTIONS: KpiSlotOption[] = [
  { value: "ORDER_SHIPMENT_LEAD_TIME", group: "D", code: "C", label: SBIZ_TYPE_LABELS.ORDER_SHIPMENT_LEAD_TIME, desc: "제작대기 → 출고/배송 시간", unit: "일", direction: "DECREASE", needsMachine: true },
  { value: "DELIVERY_COMPLIANCE_RATE", group: "D", code: "G", label: SBIZ_TYPE_LABELS.DELIVERY_COMPLIANCE_RATE, desc: "납기 준수건 / 누적 주문건 × 100", unit: "%", direction: "INCREASE", needsMachine: true },
  { value: "REVISION_REDUCTION", group: "Q", code: "J", label: SBIZ_TYPE_LABELS.REVISION_REDUCTION, desc: "제작대기 이후 edit 타입 문의 건수", unit: "건", direction: "DECREASE", needsMachine: false },
];

// 슬롯 추가 모달 지표 옵션 — SMART_FACTORY(kpiType, 정부 코드 없음 — MES 통계 전용)
const FACTORY_P_OPTIONS: KpiSlotOption[] = [
  { value: "SHEET_PRODUCTION", group: "P", label: KPI_TYPE_LABELS.SHEET_PRODUCTION, desc: "매엽지 생산량 (포스터/명함/전단지/스티커/걸이배너/스탠드배너)", unit: "장", direction: "INCREASE", needsMachine: true },
  { value: "BOOK_PRODUCTION", group: "P", label: KPI_TYPE_LABELS.BOOK_PRODUCTION, desc: "책자 생산량 (소프트커버/하드커버)", unit: "권", direction: "INCREASE", needsMachine: true },
  { value: "OPERATING_TIME", group: "P", label: KPI_TYPE_LABELS.OPERATING_TIME, desc: "실가동시간 (MES 집계, 정부 보고 없음)", unit: "시간", direction: "INCREASE", needsMachine: true },
];
const FACTORY_DQ_OPTIONS: KpiSlotOption[] = [
  { value: "ORDER_SHIPMENT_LEAD_TIME", group: "D", label: KPI_TYPE_LABELS.ORDER_SHIPMENT_LEAD_TIME, desc: "제작대기 → 출고/배송 시간", unit: "일", direction: "DECREASE", needsMachine: true },
  { value: "DELIVERY_COMPLIANCE_TIME", group: "D", label: KPI_TYPE_LABELS.DELIVERY_COMPLIANCE_TIME, desc: "납기 준수 소요 시간", unit: "일", direction: "DECREASE", needsMachine: true },
  { value: "REVISION_REDUCTION", group: "Q", label: KPI_TYPE_LABELS.REVISION_REDUCTION, desc: "제작대기 이후 edit 타입 문의 건수", unit: "건", direction: "DECREASE", needsMachine: false },
];

// ────────────────────────────────────────────────────────────────────────────

const CategoryContent: React.FC<CategoryContentProps> = ({
  program,
  companyId,
  onRemove,
  onProgramUpdated,
}) => {
  // ─── [1] 연동 기간 (program.startDate / endDate) ─────────────────────────
  const [startDate, setStartDate] = useState(program.startDate);
  const [endDate, setEndDate] = useState(program.endDate);
  // 기간 select 초기값은 저장된 start/end 로부터 역산(정확히 N년이면 해당 옵션, 아니면 CUSTOM).
  // 추가 모달에서 고른 기간(예: 1년)이 상세에서도 그대로 보이도록 동기화한다.
  const [duration, setDuration] = useState<CategoryPeriodDuration>(() =>
    inferDurationFromDates(program.startDate, program.endDate),
  );
  const isCustomDuration = duration === "CUSTOM";

  const handleStartDateChange = (next: string) => {
    setStartDate(next);
    const years = durationToYears(duration);
    if (years !== null) {
      setEndDate(addYears(next, years));
    }
  };

  const handleDurationChange = (next: CategoryPeriodDuration) => {
    setDuration(next);
    const years = durationToYears(next);
    if (years !== null) {
      setEndDate(addYears(startDate, years));
    }
  };

  // ─── [2] 보고 대상 장비 ──────────────────────────────────────────────────
  const [machines, setMachines] = useState<MachineDetailDto[]>([]);
  const [isLoadingMachines, setIsLoadingMachines] = useState(false);
  const [machinesFailed, setMachinesFailed] = useState(false);
  const [targetMachineId, setTargetMachineId] = useState<number | null>(
    program.representativeMachineId,
  );

  // program 이 바뀌면 (다른 카테고리 탭 전환 등) 로컬 상태 동기화
  useEffect(() => {
    setStartDate(program.startDate);
    setEndDate(program.endDate);
    setTargetMachineId(program.representativeMachineId);
    // 기간 select 는 저장된 start/end 로부터 역산(정확히 N년이면 해당 옵션, 아니면 직접 선택).
    setDuration(inferDurationFromDates(program.startDate, program.endDate));
  }, [
    program.id,
    program.startDate,
    program.endDate,
    program.representativeMachineId,
  ]);

  // 회사 장비 목록 로드 — adminMachineUseCase 재사용 (별도 API 없음)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoadingMachines(true);
      setMachinesFailed(false);
      try {
        const list = await adminMachineUseCase.getMachines(companyId);
        if (!cancelled) setMachines(list);
      } catch (error) {
        console.error("회사 장비 조회 실패:", error);
        if (!cancelled) {
          setMachinesFailed(true);
          setMachines([]);
        }
      } finally {
        if (!cancelled) setIsLoadingMachines(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // [1]/[2] 저장 — kpi_program 부분 수정
  // updatePlugin 은 부분수정(null/미지정=미변경) → 장비/기간 역할 분리 저장.
  const savePlugin = async (
    patch: {
      startDate?: string;
      endDate?: string;
      disclosureText?: string | null;
      clearDisclosure?: boolean;
    },
    successMsg: string,
  ) => {
    try {
      const response = await adminKpiApiService.updatePlugin(program.id, patch);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "카테고리 수정 실패");
      }
      onProgramUpdated();
      alert(successMsg);
    } catch (error) {
      console.error("카테고리 수정 실패:", error);
      const message =
        error instanceof Error ? error.message : "카테고리 수정에 실패했습니다.";
      alert(message);
    }
  };
  // [2] 대표장비(LEVEL1 보고대상) 저장 — 전용 엔드포인트 (플러그인 update 가 아님).
  // 대표는 슬롯에 측정 장비로 매핑된 장비여야 하며, 아니면 백엔드 400 → alert.
  const handleSaveMachine = async () => {
    if (targetMachineId == null) {
      alert("대표 장비를 선택해주세요.");
      return;
    }
    try {
      const response = await adminKpiApiService.setRepresentativeMachine(
        program.id,
        targetMachineId,
      );
      if (response.errorCode) {
        throw new Error(response.errorMsg || "대표 장비 지정 실패");
      }
      onProgramUpdated();
      alert("대표 장비가 저장되었습니다.");
    } catch (error) {
      console.error("대표 장비 지정 실패:", error);
      const message =
        error instanceof Error ? error.message : "대표 장비 지정에 실패했습니다.";
      alert(message);
    }
  };
  // [1] 연동 기간 저장
  const handleSavePeriod = () =>
    savePlugin({ startDate, endDate }, "연동 기간이 저장되었습니다.");
  // 변경 여부 (저장 버튼 활성/비활성)
  const machineDirty = targetMachineId !== program.representativeMachineId;
  const periodDirty =
    startDate !== program.startDate || endDate !== program.endDate;

  // ─── [3] 슬롯 (KPI1 / KPI2) ─────────────────────────────────────────────
  const [slots, setSlots] = useState<KpiSlot[]>([]);
  const [, setIsLoadingSlots] = useState(false);
  const [slotModal, setSlotModal] = useState<1 | 2 | null>(null);

  const loadSlots = useCallback(async () => {
    setIsLoadingSlots(true);
    try {
      const response = await adminKpiApiService.getSlots(program.id);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "KPI 슬롯 조회 실패");
      }
      setSlots(response.data ?? []);
    } catch (error) {
      console.error("KPI 슬롯 조회 실패:", error);
      setSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [program.id]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const kpi1Slot = useMemo(() => slots.find(isKpi1Slot) ?? null, [slots]);
  const kpi2Slot = useMemo(() => slots.find((s) => !isKpi1Slot(s)) ?? null, [slots]);

  // 카테고리 타입별 UI 분기 — WORKSHOP 은 정부(스마트제조 지원시스템 2.0) 보고용,
  // FACTORY 는 정부 보고 없이 MES 통계 전용이므로 정부 보고 UI 를 모두 숨긴다.
  const isWorkshop =
    program.pluginType === PluginType.SMART_WORKSHOP;

  // 지원사업 고지 문구 — KPI/보고 UI 없이 자유 텍스트만 편집.
  const isNotice =
    program.pluginType === PluginType.ACHIEVEMENT_NOTICE;
  const [noticeText, setNoticeText] = useState(program.disclosureText ?? "");
  const noticeDirty = noticeText !== (program.disclosureText ?? "");
  const handleSaveNotice = () =>
    savePlugin({ disclosureText: noticeText }, "고지 문구가 저장되었습니다.");

  // ─── [요약 카드] 보고 현황 요약 ────────────────────────────────────────
  const [summary, setSummary] = useState<KpiReportSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  const loadSummary = useCallback(async () => {
    setIsLoadingSummary(true);
    try {
      const response = await adminKpiApiService.getReportSummary(program.id);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "KPI 보고 요약 조회 실패");
      }
      setSummary(response.data ?? null);
    } catch (error) {
      console.error("KPI 보고 요약 조회 실패:", error);
      setSummary(null);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [program.id]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // ─── [캘린더] 보고 캘린더 (현재 월 고정) ───────────────────────────────
  const now = useMemo(() => new Date(), []);
  const calendarYear = now.getFullYear();
  const calendarMonth = now.getMonth() + 1;
  const calendarLabel = formatYearMonthLabel(calendarYear, calendarMonth);

  // 슬롯별 캘린더 cells — 각 슬롯이 정부에 별도 보고되므로 슬롯 단위로 표시.
  // LEVEL1(가동유무)은 회사 단위라 모든 슬롯 캘린더에 동일하게 반영(백엔드 처리).
  const [calendarCellsBySlotId, setCalendarCellsBySlotId] = useState<
    Record<number, KpiCalendarCell[]>
  >({});
  const [, setIsLoadingCalendar] = useState(false);

  const loadCalendar = useCallback(async () => {
    if (!isWorkshop) {
      setCalendarCellsBySlotId({});
      return;
    }
    const slotIds = [kpi1Slot?.id, kpi2Slot?.id].filter(
      (id): id is number => id != null,
    );
    if (slotIds.length === 0) {
      setCalendarCellsBySlotId({});
      return;
    }
    setIsLoadingCalendar(true);
    try {
      const responses = await Promise.all(
        slotIds.map((slotId) =>
          adminKpiApiService.getReportCalendar(
            program.id,
            calendarYear,
            calendarMonth,
            slotId,
          ),
        ),
      );
      const next: Record<number, KpiCalendarCell[]> = {};
      slotIds.forEach((slotId, idx) => {
        const r = responses[idx];
        if (r.errorCode) {
          console.error(
            `KPI 보고 캘린더 조회 실패 (slotId=${slotId}):`,
            r.errorMsg,
          );
          next[slotId] = [];
        } else {
          next[slotId] = r.data ?? [];
        }
      });
      setCalendarCellsBySlotId(next);
    } catch (error) {
      console.error("KPI 보고 캘린더 조회 실패:", error);
      setCalendarCellsBySlotId({});
    } finally {
      setIsLoadingCalendar(false);
    }
  }, [
    program.id,
    calendarYear,
    calendarMonth,
    isWorkshop,
    kpi1Slot?.id,
    kpi2Slot?.id,
  ]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // 현재 월의 1일이 어느 요일인지로 이전 달 placeholder 셀 수 계산
  const calendarLeadingPlaceholderCount = useMemo(
    () => new Date(calendarYear, calendarMonth - 1, 1).getDay(),
    [calendarYear, calendarMonth],
  );

  // ─── [최근 보고 로그] 페이징 ──────────────────────────────────────────
  const [logs, setLogs] = useState<KpiReportLog[]>([]);
  const [logsPage, setLogsPage] = useState(0);
  const [logsTotalPages, setLogsTotalPages] = useState(0);
  const [logsTotalElements, setLogsTotalElements] = useState(0);
  const [, setIsLoadingLogs] = useState(false);

  const loadLogs = useCallback(async () => {
    setIsLoadingLogs(true);
    try {
      const response = await adminKpiApiService.getReportLogs(
        program.id,
        logsPage,
        REPORT_LOGS_PAGE_SIZE,
      );
      if (response.errorCode) {
        throw new Error(response.errorMsg || "KPI 보고 로그 조회 실패");
      }
      const page = response.data;
      setLogs(page?.items ?? []);
      setLogsTotalPages(page?.totalPages ?? 0);
      setLogsTotalElements(page?.totalElements ?? 0);
    } catch (error) {
      console.error("KPI 보고 로그 조회 실패:", error);
      setLogs([]);
      setLogsTotalPages(0);
      setLogsTotalElements(0);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [program.id, logsPage]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // program 변경 시 페이지 0 으로 리셋
  useEffect(() => {
    setLogsPage(0);
  }, [program.id]);

  // ─── [스마트공장 로그 이력] — FACTORY 전용, 페이징 ───────────────────────
  const FACTORY_LOGS_PAGE_SIZE = 10;
  const isFactory = program.pluginType === PluginType.SMART_FACTORY;
  const [factoryLogs, setFactoryLogs] = useState<FactoryLog[]>([]);
  const [factoryLogsPage, setFactoryLogsPage] = useState(0);
  const [factoryLogsTotalPages, setFactoryLogsTotalPages] = useState(0);
  const [factoryLogsTotalElements, setFactoryLogsTotalElements] = useState(0);

  const loadFactoryLogs = useCallback(async () => {
    if (!isFactory) {
      setFactoryLogs([]);
      setFactoryLogsTotalPages(0);
      setFactoryLogsTotalElements(0);
      return;
    }
    try {
      const response = await adminKpiApiService.getFactoryLogs(
        program.id,
        factoryLogsPage,
        FACTORY_LOGS_PAGE_SIZE,
      );
      if (response.errorCode) {
        throw new Error(response.errorMsg || "스마트공장 로그 이력 조회 실패");
      }
      const page = response.data;
      setFactoryLogs(page?.items ?? []);
      setFactoryLogsTotalPages(page?.totalPages ?? 0);
      setFactoryLogsTotalElements(page?.totalElements ?? 0);
    } catch (error) {
      console.error("스마트공장 로그 이력 조회 실패:", error);
      setFactoryLogs([]);
      setFactoryLogsTotalPages(0);
      setFactoryLogsTotalElements(0);
    }
  }, [program.id, isFactory, factoryLogsPage]);

  useEffect(() => {
    loadFactoryLogs();
  }, [loadFactoryLogs]);

  useEffect(() => {
    setFactoryLogsPage(0);
  }, [program.id]);

  // ─── [수동 보고] ──────────────────────────────────────────────────────
  const [manualDate, setManualDate] = useState<string>(getDefaultManualReportDate());
  const [manualTarget, setManualTarget] = useState<ManualReportTarget>(
    ManualReportTarget.LEVEL1,
  );
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);

  const handleManualReport = async () => {
    if (!manualDate) {
      alert("보고 대상 일자를 선택해주세요.");
      return;
    }
    setIsSubmittingManual(true);
    try {
      const response = await adminKpiApiService.manualReport(program.id, {
        date: manualDate,
        target: manualTarget,
      });
      if (response.errorCode) {
        throw new Error(response.errorMsg || "수동 보고 실행 실패");
      }
      const result = response.data;
      if (result) {
        const shadowSuffix = result.shadow ? " (SHADOW)" : "";
        alert(
          `${result.message}${shadowSuffix}\n성공 ${result.successCount}건 · 실패 ${result.failureCount}건`,
        );
      } else {
        alert("수동 보고를 실행했습니다.");
      }
      // 결과 반영을 위해 요약/캘린더/로그 새로고침
      await Promise.all([loadSummary(), loadCalendar(), loadLogs()]);
    } catch (error) {
      console.error("수동 보고 실행 실패:", error);
      const message =
        error instanceof Error ? error.message : "수동 보고 실행에 실패했습니다.";
      alert(message);
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const handleSlotConfirm = async (
    slotKind: 1 | 2,
    value: string,
    machineIds: number[],
    opts?: { baseValue: number; goalValue: number; dailyTargetMinutes?: number },
  ) => {
    const defaults = isWorkshop
      ? DEFAULT_SLOT_DEFAULTS[value]
      : DEFAULT_FACTORY_SLOT_DEFAULTS[value];
    try {
      const response = await adminKpiApiService.createSlot(program.id, {
        sbizType: isWorkshop ? (value as SbizType) : null,
        kpiType: isWorkshop ? null : (value as KpiType),
        baseValue: opts?.baseValue ?? 0,
        goalValue: opts?.goalValue ?? 0,
        unit: defaults.unit,
        aggregationInterval: defaults.aggregationInterval,
        machineIds,
        dailyTargetMinutes: opts?.dailyTargetMinutes ?? null,
      });
      if (response.errorCode) {
        throw new Error(response.errorMsg || "KPI 슬롯 추가 실패");
      }
      await loadSlots();
      setSlotModal(null);
    } catch (error) {
      console.error("KPI 슬롯 추가 실패:", error);
      const message =
        error instanceof Error ? error.message : "KPI 슬롯 추가에 실패했습니다.";
      alert(message);
    }
  };

  const handleSlotUpdate = async (slotId: number, request: UpdateKpiSlotRequest) => {
    try {
      const response = await adminKpiApiService.updateSlot(program.id, slotId, request);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "KPI 슬롯 수정 실패");
      }
      await loadSlots();
    } catch (error) {
      console.error("KPI 슬롯 수정 실패:", error);
      const message =
        error instanceof Error ? error.message : "KPI 슬롯 수정에 실패했습니다.";
      alert(message);
    }
  };

  const handleSlotDelete = async (slotId: number) => {
    if (!window.confirm("이 KPI 지표를 삭제하시겠습니까?")) return;
    try {
      const response = await adminKpiApiService.deleteSlot(program.id, slotId);
      if (response.errorCode) {
        throw new Error(response.errorMsg || "KPI 슬롯 삭제 실패");
      }
      await loadSlots();
    } catch (error) {
      console.error("KPI 슬롯 삭제 실패:", error);
      const message =
        error instanceof Error ? error.message : "KPI 슬롯 삭제에 실패했습니다.";
      alert(message);
    }
  };

  // 카테고리 표시명
  const categoryLabel = PLUGIN_TYPE_LABELS[program.pluginType];

  // 레벨별 최근 보고 (logs 에서 가장 최신). LEVEL1 은 오늘 상태(summary) 사용.
  const latestByLevel = useMemo(() => {
    const m: Partial<Record<string, KpiReportLog>> = {};
    for (const log of logs) if (!m[log.level]) m[log.level] = log;
    return m;
  }, [logs]);

  /** 각 LEVEL 카드 하단의 실제 보고 상태 표시. */
  const renderLevelStatus = (level: "LEVEL1" | "LEVEL2" | "LEVEL3") => {
    if (level === "LEVEL1") {
      const y = summary?.todayLevel1.badge === "Y";
      return (
        <div className="flex items-center gap-1.5 mt-auto pt-2 border-t border-[#eee]">
          {y ? (
            <SuccessBadge>오늘 보고됨</SuccessBadge>
          ) : (
            <WarnBadge>오늘 미보고 (N)</WarnBadge>
          )}
          <span className="text-[11px] text-[#888]">
            {summary?.todayLevel1.reportedAt || "—"}
          </span>
        </div>
      );
    }
    const log = latestByLevel[level];
    return (
      <div className="flex items-center gap-1.5 mt-auto pt-2 border-t border-[#eee]">
        {log ? (
          log.status === "SUCCESS" ? (
            <SuccessBadge>최근 보고됨</SuccessBadge>
          ) : (
            <WarnBadge>최근 실패</WarnBadge>
          )
        ) : (
          <span className="px-2 py-[2px] rounded text-[10.5px] bg-[#F5F5F5] text-[#888]">
            보고 이력 없음
          </span>
        )}
        {log && <span className="text-[11px] text-[#888]">{log.transmittedAt}</span>}
      </div>
    );
  };

  // 지원사업 고지 문구 카테고리 — KPI/보고 UI 대신 고지 문구 편집 + 미리보기.
  if (isNotice) {
    const noticeYear = new Date().getFullYear();
    return (
      <div className="p-5">
        {/* 상단 상태 행 — 공방/공장 카테고리와 동일 (연동 해제 = 탭 삭제) */}
        <div className="flex items-center gap-2 mb-4">
          <SuccessBadge>활성</SuccessBadge>
          <span className="text-[12px] text-[#888]">회사 헤더 상단 배너</span>
          <button
            onClick={() => {
              if (window.confirm("이 카테고리를 제거하시겠습니까?")) onRemove();
            }}
            className="ml-auto text-[12px] text-[#888] hover:text-[#F24A4A]"
          >
            연동 해제
          </button>
        </div>

        <div className="border border-[#D9D9D9] rounded-[10px] p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11.5px] text-[#888] mr-0.5">자동 채움</span>
              {DISCLOSURE_TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => setNoticeText(t.build(noticeYear))}
                  className="px-2.5 py-1 text-[11.5px] rounded-full border border-[#E0E0E0] text-[#555] hover:border-[#4285F4] hover:text-[#4285F4] hover:bg-[#F5F8FF] transition-colors"
                >
                  {t.label} {noticeYear}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setNoticeText("")}
                className="px-2.5 py-1 text-[11.5px] rounded-full border border-[#E0E0E0] text-[#888] hover:border-[#F24A4A]/50 hover:text-[#F24A4A] transition-colors"
              >
                비우기
              </button>
            </div>
            <button
              type="button"
              onClick={handleSaveNotice}
              disabled={!noticeDirty}
              className="shrink-0 inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:bg-[#BBB] disabled:cursor-not-allowed"
            >
              저장
            </button>
          </div>
          <textarea
            value={noticeText}
            onChange={(e) => setNoticeText(e.target.value)}
            rows={5}
            placeholder="예) 본 프로그램(인쇄주문관리시스템)은 중소벤처기업부에서 지원하는 2026연도 중소기업 혁신바우처 사업(B20260416-29459)으로 인한 성과물임을 밝힙니다."
            className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full resize-y leading-relaxed"
          />
          {noticeText.trim() && (
            <div className="mt-4">
              <div className="text-[11.5px] text-[#888] mb-1.5">미리보기 (헤더 배너)</div>
              <div className="px-5 py-3 bg-[#4285F4] text-white font-medium text-start text-[12px] rounded-[8px] whitespace-pre-line">
                {noticeText}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* 카테고리 헤더 */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <h3 className="text-[16px] font-bold">{categoryLabel} KPI</h3>
        <SuccessBadge>활성</SuccessBadge>
        {isWorkshop && (
          <span className="text-[12px] text-[#888]">
            스마트제조 지원시스템 2.0 보고용
          </span>
        )}
        <button
          onClick={() => {
            if (window.confirm("이 카테고리를 제거하시겠습니까?")) onRemove();
          }}
          className="ml-auto text-[12px] text-[#888] hover:text-[#F24A4A]"
        >
          연동 해제
        </button>
      </div>

      {/* 요약 카드 — WORKSHOP 전용 (FACTORY 는 정부 보고 개념이 없어 요약 카드 없음) */}
      {isWorkshop && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="border border-[#D9D9D9] rounded-md p-3">
            <div className="text-[11px] text-[#888] mb-1">인증키 상태</div>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: summary?.keyStatus.dotColor ?? "#D9D9D9" }}
              />
              <span className="text-[13px] font-semibold">
                {isLoadingSummary && !summary
                  ? "로딩 중…"
                  : (summary?.keyStatus.label ?? "—")}
              </span>
            </div>
          </div>
          <div className="border border-[#D9D9D9] rounded-md p-3">
            <div className="text-[11px] text-[#888] mb-1">만료까지</div>
            <div className="text-[13px] font-semibold">
              {summary?.daysUntilExpire ?? "—"}
            </div>
          </div>
          <div className="border border-[#D9D9D9] rounded-md p-3">
            <div className="text-[11px] text-[#888] mb-1">오늘 LEVEL1</div>
            <div className="text-[13px] font-semibold">
              {summary ? (
                summary.todayLevel1.badge === "Y" ? (
                  <SuccessBadge>{summary.todayLevel1.badge}</SuccessBadge>
                ) : (
                  <WarnBadge>{summary.todayLevel1.badge}</WarnBadge>
                )
              ) : (
                <span className="text-[#888]">—</span>
              )}{" "}
              <span className="text-[#888] text-[11px] ml-1">
                {summary?.todayLevel1.reportedAt ?? ""}
              </span>
            </div>
          </div>
          <div className="border border-[#D9D9D9] rounded-md p-3">
            <div className="text-[11px] text-[#888] mb-1">금월 보고 성공률</div>
            <div className="text-[13px] font-semibold">
              {summary?.monthlySuccess ?? "—"}
            </div>
          </div>
        </div>
      )}

      {/* 인증키(원문) — 가입 시 최초 1회만 입력되고 이후 수정 API가 없어 조회 전용으로 노출 */}
      {(isWorkshop || program.pluginType === PluginType.SMART_FACTORY) && (
        <div className="border border-[#D9D9D9] rounded-[10px] p-4 mb-4">
          <div className="text-[12px] text-[#888] mb-1">인증키</div>
          <div className="text-[13px] font-mono">
            {program.certKey ?? <span className="text-[#BBB]">미등록</span>}
          </div>
        </div>
      )}

      {/* KPI 보고 설정 (사업 상세 단위 — 보고 채널/자동 보고) — WORKSHOP 전용 */}
      {isWorkshop && <KpiReportSettingsSection pluginId={program.id} />}

      {/* 로그 API 전송 설정 — FACTORY 전용 */}
      {program.pluginType === PluginType.SMART_FACTORY && (
        <FactoryLogSettingsSection program={program} onProgramUpdated={onProgramUpdated} />
      )}

      {/* [1] 연동 기간 */}
      <div className="border border-[#D9D9D9] rounded-[10px] p-5 mb-4">
        <div className="mb-4">
          <h4 className="text-[14px] font-semibold mb-1">
            {isWorkshop ? "스마트제조 지원시스템 2.0 연동 기간" : "MES 연동 기간"}
          </h4>
          <p className="text-[12px] text-[#888]">
            {isWorkshop
              ? "시작 일자부터 만료 일자까지 자동 보고가 활성화됩니다."
              : "시작 일자부터 만료 일자까지 MES 통계 집계가 활성화됩니다."}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">
              시작 일자 <span className="text-[#F24A4A]">*</span>
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full"
            />
            <p className="text-[11.5px] text-[#888] mt-1.5">
              카테고리 생성일과 별개 · 보고 활성 시작 시점
            </p>
          </div>
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">기간</label>
            <select
              value={duration}
              onChange={(e) =>
                handleDurationChange(e.target.value as CategoryPeriodDuration)
              }
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full"
            >
              {CATEGORY_PERIOD_DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-[11.5px] text-[#888] mt-1.5">
              N년 선택 시 만료일 자동 계산
            </p>
          </div>
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">
              만료 일자 <span className="text-[#F24A4A]">*</span>
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={!isCustomDuration}
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full disabled:bg-[#F5F5F5] disabled:text-[#BBBBBB] disabled:cursor-not-allowed"
            />
            <p className="text-[11.5px] text-[#888] mt-1.5">
              만료 도래 시 자동 보고 중단
            </p>
          </div>
        </div>
      </div>

      {/* [2] 보고/집계 대상 장비 — 회사(사업)당 1대(company-wide). WORKSHOP/FACTORY 동일한 방식. */}
      <div className="border border-[#D9D9D9] rounded-[10px] p-5 mb-4">
        <div className="mb-4">
          <h4 className="text-[14px] font-semibold mb-1">
            {isWorkshop ? "보고 대상 장비" : "집계 대상 장비"}
          </h4>
          <p className="text-[12px] text-[#888]">
            {isWorkshop ? (
              <>
                하루 1회 이상{" "}
                <code className="px-1 bg-[#F5F5F5] rounded font-mono text-[10.5px]">
                  WORKING
                </code>{" "}
                상태가 감지되면 Y, 없으면 23:55 에 N 으로 전송. N 인 날은 LEVEL2/3 보고도 함께
                미전송됩니다.
              </>
            ) : (
              <>MES KPI 통계를 집계할 대표 장비 1대를 선택합니다. 이 회사가 등록한 장비 목록을 확인할 수 있습니다.</>
            )}
          </p>
        </div>
        {/* 한 줄: 대표 장비 select + 저장 (지정 후 바로 저장해야 슬롯 등록 가능) */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={targetMachineId ?? ""}
            onChange={(e) =>
              setTargetMachineId(e.target.value === "" ? null : Number(e.target.value))
            }
            disabled={isLoadingMachines}
            className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white flex-1 min-w-[280px] disabled:bg-[#F5F5F5]"
          >
            <option value="">대표 장비 선택 안 함</option>
            {machinesFailed
              ? MOCK_CATEGORY_TARGET_EQUIPMENT_OPTIONS.map((o) => (
                  <option key={o.ip} value="" disabled>
                    {o.label} ({o.ip}) — 장비 API 불러오기 실패
                  </option>
                ))
              : machines.map((m) => (
                  <option key={m.machineId} value={m.machineId}>
                    {m.manufacturer} {m.modelName} ({m.ipAddress})
                  </option>
                ))}
          </select>
          <button
            type="button"
            onClick={handleSaveMachine}
            disabled={!machineDirty}
            className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#2563EB] text-white hover:bg-[#1D4ED8] shrink-0 disabled:bg-[#BBB] disabled:cursor-not-allowed"
          >
            저장
          </button>
        </div>
        {isLoadingMachines && (
          <p className="text-[11.5px] text-[#888] mt-1.5">장비 목록을 불러오는 중…</p>
        )}
        {machinesFailed && (
          <p className="text-[11.5px] text-[#F24A4A] mt-1.5">
            장비 목록을 불러오지 못했습니다.
          </p>
        )}
      </div>

      {/* [3] 보고 KPI 항목 — 2슬롯 */}
      <div className="border border-[#D9D9D9] rounded-[10px] p-5 mb-4">
        <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h4 className="text-[14px] font-semibold mb-1">
              {isWorkshop ? "보고 KPI 항목" : "KPI 항목 (MES 통계)"}
            </h4>
            <p className="text-[12px] text-[#888]">
              {isWorkshop ? (
                <>
                  스마트 공방은{" "}
                  <strong>KPI 1 (P 그룹) + KPI 2 (D 또는 Q 그룹)</strong> 두 지표를 보고합니다. ＋
                  버튼으로 지표를 선택하고, base/목표 등 입력값은 우상단 <strong>편집</strong>{" "}
                  버튼을 눌러야 수정할 수 있습니다.
                </>
              ) : (
                <>
                  스마트 공장은{" "}
                  <strong>KPI 1 (P 그룹) + KPI 2 (D 또는 Q 그룹)</strong> 두 지표를 MES 통계용으로
                  집계합니다. ＋ 버튼으로 지표를 선택하고, base/목표 등 입력값은
                  우상단 <strong>편집</strong> 버튼을 눌러야 수정할 수 있습니다.
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-[#888] whitespace-nowrap">
            <BlueBadge>P</BlueBadge>생산성
            <span className="mx-0.5">·</span>
            <BlueBadge>D</BlueBadge>납기
            <span className="mx-0.5">·</span>
            <BlueBadge>Q</BlueBadge>품질
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* KPI 1 슬롯 (P 그룹) — 보고 캘린더는 WORKSHOP 에서만 슬롯별로 표시 (정부 보고 이력) */}
          {kpi1Slot ? (
            <KpiSlotCard
              slot={kpi1Slot}
              tier={1}
              onUpdate={handleSlotUpdate}
              onDelete={handleSlotDelete}
              calendar={
                isWorkshop && kpi1Slot.id != null
                  ? {
                      label: calendarLabel,
                      cells: calendarCellsBySlotId[kpi1Slot.id] ?? [],
                      leadingPlaceholderCount: calendarLeadingPlaceholderCount,
                    }
                  : undefined
              }
            />
          ) : (
            <EmptySlotCard
              tier={1}
              onAddClick={() => setSlotModal(1)}
              hintLine1="P 그룹 (생산성)"
              hintLine2="에서 1개의 지표를 선택하세요"
            />
          )}

          {/* KPI 2 슬롯 (D/Q 그룹) — 보고 캘린더는 WORKSHOP 에서만 슬롯별로 표시 (정부 보고 이력) */}
          {kpi2Slot ? (
            <KpiSlotCard
              slot={kpi2Slot}
              tier={2}
              onUpdate={handleSlotUpdate}
              onDelete={handleSlotDelete}
              calendar={
                isWorkshop && kpi2Slot.id != null
                  ? {
                      label: calendarLabel,
                      cells: calendarCellsBySlotId[kpi2Slot.id] ?? [],
                      leadingPlaceholderCount: calendarLeadingPlaceholderCount,
                    }
                  : undefined
              }
            />
          ) : (
            <EmptySlotCard
              tier={2}
              onAddClick={() => setSlotModal(2)}
              hintLine1="D 그룹 (납기) 또는 Q 그룹 (품질)"
              hintLine2="에서 1개의 지표를 선택하세요"
            />
          )}
        </div>
      </div>

      {/* KPI 추이 그래프 — 등록된 슬롯(최대 2개) 기준, 어드민 compute */}
      <AdminKpiTrendSection
        companyId={companyId}
        kpi1Slot={kpi1Slot}
        kpi2Slot={kpi2Slot}
        targetMachineId={program.representativeMachineId}
      />

      {/* [4] 보고 스케줄 안내 — WORKSHOP 전용 (정부 보고 스케줄) */}
      {isWorkshop && (
      <div className="border border-[#D9D9D9] rounded-[10px] p-5 mb-4">
        <h4 className="text-[14px] font-semibold mb-3">보고 스케줄</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-[#E0E0E0] rounded-[10px] p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <BlueBadge>LEVEL1</BlueBadge>
                <span className="text-[13px] font-semibold">가동유무</span>
              </div>
              <span className="text-[10.5px] px-2 py-[2px] rounded-full bg-[#E8EFFF] text-[#4285F4] font-medium whitespace-nowrap">
                이벤트 즉시
              </span>
            </div>
            <div className="text-[11.5px] text-[#555]">
              WORKING 첫 감지 시 Y 1회, 미발생 시 23:55 N
            </div>
            {renderLevelStatus("LEVEL1")}
          </div>
          <div className="border border-[#E0E0E0] rounded-[10px] p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <BlueBadge>LEVEL2</BlueBadge>
                <span className="text-[13px] font-semibold">성과</span>
              </div>
              <span className="text-[10.5px] px-2 py-[2px] rounded-full bg-[#E8EFFF] text-[#4285F4] font-medium whitespace-nowrap">
                23:55 일배치
              </span>
            </div>
            <div className="text-[11.5px] text-[#555]">일별 계산값</div>
            <div className="text-[11px] text-[#888] mt-1">· LEVEL1=Y 인 날만 전송</div>
            {renderLevelStatus("LEVEL2")}
          </div>
          <div className="border border-[#E0E0E0] rounded-[10px] p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <BlueBadge>LEVEL3</BlueBadge>
                <span className="text-[13px] font-semibold">검수</span>
              </div>
              <span className="text-[10.5px] px-2 py-[2px] rounded-full bg-[#E8EFFF] text-[#4285F4] font-medium whitespace-nowrap">
                23:55 일배치
              </span>
            </div>
            <div className="text-[11.5px] text-[#555]">시간/분 단위 원본</div>
            <div className="text-[11px] text-[#888] mt-1">· LEVEL1=Y 인 날만 전송</div>
            {renderLevelStatus("LEVEL3")}
          </div>
        </div>
      </div>
      )}

      {/* [5] 수동 보고 — WORKSHOP 전용 (정부 보고 재전송) */}
      {isWorkshop && (
      <div className="border border-[#D9D9D9] rounded-[10px] p-5 mb-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h4 className="text-[14px] font-semibold mb-1">KPI 수동 보고</h4>
            <p className="text-[12px] text-[#888]">
              지정한 과거 일자의 KPI 데이터를 다시 계산하여 즉시 송신합니다.
            </p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[11px] text-[#555] block mb-1">대상 일자</label>
              <input
                type="date"
                value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
                disabled={isSubmittingManual}
                className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white disabled:bg-[#F5F5F5]"
              />
            </div>
            <div>
              <label className="text-[11px] text-[#555] block mb-1">보고 항목</label>
              <select
                value={manualTarget}
                onChange={(e) =>
                  setManualTarget(e.target.value as ManualReportTarget)
                }
                disabled={isSubmittingManual}
                className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white disabled:bg-[#F5F5F5]"
              >
                {MANUAL_REPORT_TARGET_OPTIONS.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <button
              onClick={handleManualReport}
              disabled={isSubmittingManual}
              className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] disabled:bg-[#F5F5F5] disabled:text-[#888] disabled:cursor-not-allowed"
            >
              {isSubmittingManual ? "실행 중…" : "수동 보고 실행"}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* [6] 최근 보고 로그 — WORKSHOP 전용 (kpi_report_log) */}
      {isWorkshop && (
      <div className="border border-[#D9D9D9] rounded-[10px] mb-4 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#D9D9D9] bg-[#F8F8F8]">
          <h4 className="text-[13px] font-semibold">최근 보고 로그</h4>
          <p className="text-[11.5px] text-[#888] mt-0.5">
            kpi_report_log · status / performance_rate / measured_value / measured_at /
            transmitted_at
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  전송 시각
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  LEVEL
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  KPI 항목
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-right border-b border-[#D9D9D9]">
                  측정값
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-right border-b border-[#D9D9D9]">
                  성과율
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  상태
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="py-[24px] px-[18px] text-center text-[12.5px] text-[#888]"
                  >
                    보고 로그가 없습니다.
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => {
                  const isLast = idx === logs.length - 1;
                  const tdBorder = isLast ? "" : "border-b border-[#D9D9D9]";
                  return (
                    <tr key={`${log.transmittedAt}-${log.level}-${idx}`}>
                      <td className={`py-[11.5px] px-[18px] text-[13px] text-[#555] ${tdBorder}`}>
                        {log.transmittedAt}
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] ${tdBorder}`}>
                        <BlueBadge>{log.level}</BlueBadge>
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] ${tdBorder}`}>
                        {log.item}
                      </td>
                      <td
                        className={`py-[11.5px] px-[18px] text-[13px] text-right ${
                          log.measuredValue !== "—" ? "font-medium" : ""
                        } ${tdBorder}`}
                      >
                        {log.measuredValue}
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] text-right ${tdBorder}`}>
                        {log.performanceRate}
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] ${tdBorder}`}>
                        <ReportLogStatusBadge status={log.status} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {logsTotalPages > 1 && (
          <div className="px-5 py-3 border-t border-[#D9D9D9] flex items-center justify-between text-[12px] text-[#555]">
            <div>
              전체 {logsTotalElements.toLocaleString()}건 · {logsPage + 1} / {logsTotalPages} 페이지
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setLogsPage((p) => Math.max(0, p - 1))}
                disabled={logsPage <= 0}
                className="px-3 py-1 rounded-md border border-[#D9D9D9] bg-white hover:bg-[#F5F5F5] disabled:bg-[#F5F5F5] disabled:text-[#BBB] disabled:cursor-not-allowed"
              >
                이전
              </button>
              <button
                onClick={() =>
                  setLogsPage((p) => Math.min(logsTotalPages - 1, p + 1))
                }
                disabled={logsPage >= logsTotalPages - 1}
                className="px-3 py-1 rounded-md border border-[#D9D9D9] bg-white hover:bg-[#F5F5F5] disabled:bg-[#F5F5F5] disabled:text-[#BBB] disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* [6] 스마트공장 로그 API 전송 이력 — FACTORY 전용 (plugin_factory_log) */}
      {isFactory && (
      <div className="border border-[#D9D9D9] rounded-[10px] mb-4 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#D9D9D9] bg-[#F8F8F8]">
          <h4 className="text-[13px] font-semibold">로그 API 전송 이력</h4>
          <p className="text-[11.5px] text-[#888] mt-0.5">
            plugin_factory_log · 10분 배치로 로그인/워크로드/주문수정/상품삭제 활동을 전송
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  전송 시각
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  활동
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-right border-b border-[#D9D9D9]">
                  데이터량
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  응답코드
                </th>
                <th className="bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-[11.5px] px-[18px] text-left border-b border-[#D9D9D9]">
                  상태
                </th>
              </tr>
            </thead>
            <tbody>
              {factoryLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="py-[24px] px-[18px] text-center text-[12.5px] text-[#888]"
                  >
                    전송 이력이 없습니다.
                  </td>
                </tr>
              ) : (
                factoryLogs.map((log, idx) => {
                  const isLast = idx === factoryLogs.length - 1;
                  const tdBorder = isLast ? "" : "border-b border-[#D9D9D9]";
                  return (
                    <tr key={log.id}>
                      <td className={`py-[11.5px] px-[18px] text-[13px] text-[#555] ${tdBorder}`}>
                        {log.transmittedAt ?? log.createdAt}
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] ${tdBorder}`}>
                        <BlueBadge>{FACTORY_LOG_USE_SE_LABELS[log.useSe] ?? log.useSe}</BlueBadge>
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] text-right ${tdBorder}`}>
                        {log.dataUsgqty.toLocaleString()}
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] text-[#555] ${tdBorder}`}>
                        {log.recptnRsltCd ?? "—"}
                      </td>
                      <td className={`py-[11.5px] px-[18px] text-[13px] ${tdBorder}`}>
                        <FactoryLogStatusBadge status={log.status} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {factoryLogsTotalPages > 1 && (
          <div className="px-5 py-3 border-t border-[#D9D9D9] flex items-center justify-between text-[12px] text-[#555]">
            <div>
              전체 {factoryLogsTotalElements.toLocaleString()}건 · {factoryLogsPage + 1} / {factoryLogsTotalPages} 페이지
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setFactoryLogsPage((p) => Math.max(0, p - 1))}
                disabled={factoryLogsPage <= 0}
                className="px-3 py-1 rounded-md border border-[#D9D9D9] bg-white hover:bg-[#F5F5F5] disabled:bg-[#F5F5F5] disabled:text-[#BBB] disabled:cursor-not-allowed"
              >
                이전
              </button>
              <button
                onClick={() =>
                  setFactoryLogsPage((p) => Math.min(factoryLogsTotalPages - 1, p + 1))
                }
                disabled={factoryLogsPage >= factoryLogsTotalPages - 1}
                className="px-3 py-1 rounded-md border border-[#D9D9D9] bg-white hover:bg-[#F5F5F5] disabled:bg-[#F5F5F5] disabled:text-[#BBB] disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* 하단 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-[11.5px] text-[#888]">
          {isWorkshop
            ? "P 그룹 KPI 1개 이상 선택 필수 · 인증키 만료 시 자동 보고 중단"
            : ""}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setStartDate(program.startDate);
              setEndDate(program.endDate);
              setTargetMachineId(program.representativeMachineId);
              setDuration("CUSTOM");
            }}
            className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB]"
          >
            취소
          </button>
          <button
            onClick={handleSavePeriod}
            disabled={!periodDirty}
            className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:bg-[#BBB] disabled:cursor-not-allowed"
          >
            저장
          </button>
        </div>
      </div>

      {slotModal !== null && (
        <KpiSlotModal
          slot={slotModal}
          options={
            isWorkshop
              ? slotModal === 1 ? WORKSHOP_P_OPTIONS : WORKSHOP_DQ_OPTIONS
              : slotModal === 1 ? FACTORY_P_OPTIONS : FACTORY_DQ_OPTIONS
          }
          existingValues={slots
            .map((s) => (isWorkshop ? s.sbizType : (s.kpiType as string)))
            .filter((v): v is NonNullable<typeof v> => v != null)}
          machines={machines.map((m) => ({
            machineId: m.machineId,
            modelName: m.modelName,
            machineType: m.machineType,
            ipAddress: m.ipAddress,
          }))}
          targetMachineId={program.representativeMachineId}
          onClose={() => setSlotModal(null)}
          onConfirm={handleSlotConfirm}
        />
      )}
    </div>
  );
};

// ─── 로그 API 전송 설정 (FACTORY 전용) ──────────────────────────────────────
// sbiz24 와 달리 별도 설정 API가 아니라 updatePlugin 에 factoryLogApiEnabled/
// factoryTestMode/factoryAutoFallback 을 직접 실어보낸다(설정을 detail에 통합).
const FactoryLogSettingsSection: React.FC<{
  program: Plugin;
  onProgramUpdated: () => void;
}> = ({ program, onProgramUpdated }) => {
  const [logApiEnabled, setLogApiEnabled] = useState(program.factoryLogApiEnabled);
  const [testMode, setTestMode] = useState(program.factoryTestMode);
  const [autoFallback, setAutoFallback] = useState(program.factoryAutoFallback);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLogApiEnabled(program.factoryLogApiEnabled);
    setTestMode(program.factoryTestMode);
    setAutoFallback(program.factoryAutoFallback);
  }, [program.id, program.factoryLogApiEnabled, program.factoryTestMode, program.factoryAutoFallback]);

  const dirty =
    logApiEnabled !== program.factoryLogApiEnabled ||
    testMode !== program.factoryTestMode ||
    autoFallback !== program.factoryAutoFallback;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await adminKpiApiService.updatePlugin(program.id, {
        factoryLogApiEnabled: logApiEnabled,
        factoryTestMode: testMode,
        factoryAutoFallback: autoFallback,
      });
      if (response.errorCode) throw new Error(response.errorMsg || "설정 저장 실패");
      onProgramUpdated();
      alert("저장되었습니다.");
    } catch (error) {
      console.error("로그 API 설정 저장 실패:", error);
      alert(error instanceof Error ? error.message : "설정 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="mb-5 border border-[#D9D9D9] rounded-[10px] bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#D9D9D9] flex items-center justify-between bg-[#FAFAFA]">
        <div>
          <h3 className="text-[14px] font-semibold">로그 API 전송 설정</h3>
          <p className="text-[11.5px] text-[#888] mt-0.5">
            스마트공장 사업관리시스템 로그 수집 API 전송 · 10분 배치
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !dirty}
          className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#252525] text-white hover:bg-[#333] disabled:bg-[#BBB] disabled:cursor-not-allowed"
        >
          {isSaving ? "저장 중…" : "설정 저장"}
        </button>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <FactorySettingToggleCard
          label="로그 API 전송"
          description="OFF 일 경우 활동이 감지돼도 정부 로그 API 로 전송하지 않습니다."
          value={logApiEnabled}
          onChange={setLogApiEnabled}
        />
        <FactorySettingToggleCard
          label="테스트 모드"
          description="ON 일 경우 실제 전송 없이 SHADOW 로만 기록합니다."
          value={testMode}
          onChange={setTestMode}
        />
        <FactorySettingToggleCard
          label="자동 폴백"
          description="1시간 이상 활동이 없으면 더미 로그인 1건을 자동 전송합니다."
          value={autoFallback}
          onChange={setAutoFallback}
        />
      </div>
    </section>
  );
};

const FactorySettingToggleCard: React.FC<{
  label: string;
  description: string;
  value: boolean;
  onChange: (next: boolean) => void;
}> = ({ label, description, value, onChange }) => (
  <label
    className={`border rounded-md p-3 flex items-start gap-3 cursor-pointer transition-colors ${
      value
        ? "border-[#4285F4] bg-[#F5F8FF]"
        : "border-[#D9D9D9] bg-white hover:bg-[#FAFAFA]"
    }`}
  >
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-4 h-4 accent-[#4285F4] flex-shrink-0"
    />
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold text-[#252525]">{label}</div>
      <div className="text-[11.5px] text-[#888] mt-0.5 leading-relaxed">
        {description}
      </div>
    </div>
  </label>
);

// ─── 슬롯 카드 (채워진 슬롯, 편집 모드 지원) ────────────────────────────────
interface KpiSlotCardCalendar {
  label: string;
  cells: KpiCalendarCell[];
  leadingPlaceholderCount: number;
}

interface KpiSlotCardProps {
  slot: KpiSlot;
  tier: 1 | 2;
  onUpdate: (slotId: number, request: UpdateKpiSlotRequest) => Promise<void>;
  onDelete: (slotId: number) => Promise<void>;
  /** KPI1 슬롯에만 전달. 없으면 캘린더 영역 미표시. */
  calendar?: KpiSlotCardCalendar;
}

const KpiSlotCard: React.FC<KpiSlotCardProps> = ({
  slot,
  tier,
  onUpdate,
  onDelete,
  calendar,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [baseValue, setBaseValue] = useState<string>(String(slot.baseValue));
  const [goalValue, setGoalValue] = useState<string>(String(slot.goalValue));
  const [unit, setUnit] = useState<string>(slot.unit);
  const [aggregationInterval, setAggregationInterval] = useState<AggregationInterval>(
    slot.aggregationInterval,
  );

  useEffect(() => {
    setBaseValue(String(slot.baseValue));
    setGoalValue(String(slot.goalValue));
    setUnit(slot.unit);
    setAggregationInterval(slot.aggregationInterval);
  }, [
    slot.id,
    slot.baseValue,
    slot.goalValue,
    slot.unit,
    slot.aggregationInterval,
  ]);

  const handleToggle = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }
    // 완료 → 저장
    const parsedBase = Number(baseValue);
    const parsedGoal = Number(goalValue);
    if (Number.isNaN(parsedBase) || Number.isNaN(parsedGoal)) {
      alert("이전값/목표값을 숫자로 입력해주세요.");
      return;
    }
    await onUpdate(slot.id, {
      baseValue: parsedBase,
      goalValue: parsedGoal,
      unit,
      aggregationInterval,
    });
    setIsEditing(false);
  };

  const groupLabel = tier === 1 ? "P 그룹 (생산성)" : "D·Q 그룹 (납기·품질)";
  const tierLabel = tier === 1 ? "KPI 1" : "KPI 2";
  const indicatorName = slot.sbizType != null ? SBIZ_TYPE_LABELS[slot.sbizType] : KPI_TYPE_LABELS[slot.kpiType];

  return (
    <div
      className="rounded-[10px] p-4"
      style={{ border: "1px solid rgba(66,133,244,0.4)", background: "#F5F8FF" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] font-bold text-[#4285F4]">{tierLabel}</span>
          <span className="text-[11px] text-[#888]">{groupLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleToggle}
            className="text-[11.5px] text-[#4285F4] hover:bg-white px-2 py-1 rounded"
          >
            {isEditing ? "완료" : "편집"}
          </button>
          <button
            onClick={() => onDelete(slot.id)}
            className="text-[11.5px] text-[#888] hover:text-[#F24A4A] hover:bg-white px-2 py-1 rounded"
          >
            삭제
          </button>
        </div>
      </div>
      <div
        className="mb-3 pb-3"
        style={{ borderBottom: "1px solid rgba(217,217,217,0.6)" }}
      >
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <span className="font-semibold text-[14px]">{indicatorName}</span>
          {slot.kpiFldCd != null && (
            <span
              className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
              style={{ background: "#E8EFFF", color: "#4285F4" }}
            >
              {slot.kpiFldCd}
            </span>
          )}
          {slot.kpiDtlCd != null && (
            <span
              className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
              style={{ background: "#E8EFFF", color: "#4285F4" }}
            >
              코드 {slot.kpiDtlCd}
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#888]">
          {slot.sbizType != null ? "sbiz_type" : "kpi_type"}:{" "}
          <code className="px-1 bg-white rounded font-mono text-[10px]">
            {slot.sbizType ?? slot.kpiType}
          </code>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <label className="text-[11px] text-[#555] block mb-1">이전값 (base)</label>
          <input
            type="number"
            value={baseValue}
            onChange={(e) => setBaseValue(e.target.value)}
            disabled={!isEditing}
            className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[12.5px] text-right outline-none focus:border-[#4285F4] w-full disabled:bg-[#F2F2F2] disabled:text-[#555]"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#555] block mb-1">목표값 (goal)</label>
          <input
            type="number"
            value={goalValue}
            onChange={(e) => setGoalValue(e.target.value)}
            disabled={!isEditing}
            className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[12.5px] text-right outline-none focus:border-[#4285F4] w-full disabled:bg-[#F2F2F2] disabled:text-[#555]"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#555] block mb-1">단위</label>
          <input
            type="text"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={!isEditing}
            className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-[#4285F4] w-full disabled:bg-[#F2F2F2] disabled:text-[#555]"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#555] block mb-1">집계 주기</label>
          <select
            value={aggregationInterval}
            onChange={(e) =>
              setAggregationInterval(e.target.value as AggregationInterval)
            }
            disabled={!isEditing}
            className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-[#4285F4] w-full disabled:bg-[#F2F2F2] disabled:text-[#555] bg-white"
          >
            {Object.values(AggregationInterval).map((interval) => (
              <option key={interval} value={interval}>
                {AGGREGATION_INTERVAL_LABELS[interval]} ({interval})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 측정 대상 장비(합산) */}
      {slot.dailyTargetMinutes != null && (
        <div className="mb-3 text-[11.5px] text-[#555]">
          하루 가동 목표{" "}
          <b className="text-[#4285F4]">{slot.dailyTargetMinutes / 60}시간</b>
          <span className="text-[#888]"> · 가동률 = 가동시간 ÷ 목표시간 (그래프는 시간)</span>
        </div>
      )}

      {slot.machines.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] text-[#555] mb-1">
            측정 대상 장비 · 합산 {slot.machines.length}대
          </div>
          <div className="flex flex-wrap gap-1.5">
            {slot.machines.map((m) => (
              <span
                key={m.id}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] ${
                  m.isRepresentative
                    ? "bg-[#E8EFFF] border-[#4285F4] text-[#4285F4]"
                    : "bg-[#F5F8FF] border-[#E0E0E0] text-[#555]"
                }`}
              >
                {m.isRepresentative && "★ "}
                {m.name}
                <span className="text-[#aaa]">{m.ipAddress}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 보고 캘린더 — GET /program/{id}/calendar?year=&month= (KPI1 슬롯에만 표시) */}
      {calendar && (
        <div className="pt-3" style={{ borderTop: "1px solid rgba(217,217,217,0.6)" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-[#555] font-medium">
              보고 캘린더 · {calendar.label}
            </span>
            <span className="text-[10px] text-[#888] flex items-center gap-1.5">
              <span className="flex items-center gap-0.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: "#27AE60" }}
                />
                성공
              </span>
              <span className="flex items-center gap-0.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: "#F24A4A" }}
                />
                실패
              </span>
              <span className="flex items-center gap-0.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: "#E0E0E0" }}
                />
                미보고
              </span>
            </span>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[10px] text-center">
            {["일", "월", "화", "수", "목", "금", "토"].map((d) => (
              <span key={d} className="text-[#888]">
                {d}
              </span>
            ))}
            {Array.from({ length: calendar.leadingPlaceholderCount }).map((_, idx) => (
              <span key={`pad-${idx}`} className="py-0.5" />
            ))}
            {calendar.cells.map((cell) => {
              const isSuccessOrFailure =
                cell.status === "SUCCESS" || cell.status === "FAILURE";
              if (cell.status === "EMPTY") {
                return (
                  <span key={`day-${cell.day}`} className="text-[#D9D9D9] py-0.5">
                    {cell.day}
                  </span>
                );
              }
              return (
                <span
                  key={`day-${cell.day}`}
                  className={`rounded py-0.5 ${
                    isSuccessOrFailure ? "text-white" : "text-[#888]"
                  }`}
                  style={{ background: CALENDAR_BG[cell.status] }}
                >
                  {cell.day}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── 빈 슬롯 카드 (＋ 추가 버튼) ──────────────────────────────────────────
interface EmptySlotCardProps {
  tier: 1 | 2;
  onAddClick: () => void;
  hintLine1: string;
  hintLine2: string;
}

const EmptySlotCard: React.FC<EmptySlotCardProps> = ({
  tier,
  onAddClick,
  hintLine1,
  hintLine2,
}) => (
  <div
    className="rounded-[10px] p-4 flex flex-col items-center justify-center text-center min-h-[440px]"
    style={{ border: "2px dashed rgba(66,133,244,0.3)" }}
  >
    <button
      onClick={onAddClick}
      className="w-14 h-14 rounded-full text-[#4285F4] text-[24px] hover:bg-[#F5F8FF] flex items-center justify-center mb-3 transition-colors"
      style={{ border: "2px dashed #4285F4" }}
    >
      ＋
    </button>
    <div className="text-[13px] font-semibold text-[#252525]">
      KPI {tier} — 지표 추가
    </div>
    <div className="text-[11.5px] text-[#888] mt-1.5 leading-relaxed">
      {hintLine1}
      <br />
      {hintLine2}
    </div>
  </div>
);

export default CategoryContent;